'use client';

import { ReactNode } from 'react';

export interface NavbarProps {
  children: ReactNode;
  isBordered?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  position?: 'static' | 'sticky' | 'fixed';
}

const maxWidthClasses = {
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-md',
  lg: 'max-w-screen-lg',
  xl: 'max-w-screen-xl',
  '2xl': 'max-w-screen-2xl',
  full: 'max-w-full',
};

const NavbarRoot = ({
  children,
  isBordered = true,
  maxWidth = 'full',
  position = 'sticky',
}: NavbarProps) => {
  const positionClass =
    position === 'sticky'
      ? 'sticky top-0'
      : position === 'fixed'
        ? 'fixed top-0'
        : 'static';
  const borderClass = isBordered ? 'border-b border-divider/50' : '';
  const maxWidthClass =
    maxWidth !== 'full' ? `${maxWidthClasses[maxWidth]} mx-auto` : '';

  return (
    <nav
      className={`${positionClass} z-50 w-full ${borderClass} bg-background/70 backdrop-blur-lg transition-all duration-300`}
    >
      <header
        className={`flex h-16 items-center justify-between px-6 ${maxWidthClass}`}
      >
        {children}
      </header>
    </nav>
  );
};

const Brand = ({ children }: { children: ReactNode }) => (
  <div className="flex items-center gap-3 shrink-0">{children}</div>
);

const Content = ({
  children,
  justify = 'start',
}: {
  children: ReactNode;
  justify?: 'start' | 'center' | 'end';
}) => {
  const justifyClass = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
  }[justify];

  return (
    <div className={`flex items-center gap-6 flex-1 ${justifyClass}`}>
      {children}
    </div>
  );
};

const Item = ({
  children,
  isActive,
}: {
  children: ReactNode;
  isActive?: boolean;
}) => (
  <div
    className={`flex items-center h-full ${isActive ? 'font-semibold text-primary' : 'text-foreground/70'}`}
  >
    {children}
  </div>
);

export const Navbar = Object.assign(NavbarRoot, {
  Brand,
  Content,
  Item,
});
